import { Annotation } from "../../tools/index.js"
import { Rectangle } from "../primitives"
import { Format } from "./format"
import { Stroke } from "./stroke"

/**
 * @returns {Number} Returns the integer part of value, without decimals, rounded to lower.
 */
function trunc(value) {
	return value | 0
}

/**
 * @memberOf Tee.Chart
 * @constructor
 * @class Defines a scale from minimum to maximum, to transform series points into chart canvas pixels coordinates.
 * @param {Tee.Chart} chart The chart object this axis object belongs to.
 * @param {Boolean} horizontal Determines if axis is horizontal or vertical.
 * @param {Boolean} otherSide Determines if axis is at top/right or bottom/left side of chart.
 * @property {Format} format Visual properties to draw the axis line.
 * @property {Tee.Chart.Axis-Labels} labels Properties to display axis labels at tick increments.
 * @property {Tee.Chart.Axis-Grid} grid Properties to display grid lines at tick increments.
 * @property {Tee.Chart.Axis-Ticks} ticks Properties to display tick lines at each increment.
 * @property {Tee.Chart.Axis-Ticks} innerTicks Properties to display tick lines at each increment, inside chart.
 * @property {Tee.Chart.Axis-Ticks} minorTicks Properties to display small tick lines between ticks.
 * @property {Tee.Chart.Axis-Title} title Properties to display text that describes the axis.
 */
let Axis = function Axis(chart, horizontal, otherSide) {
	this.chart = chart
	this.visible = true
	this.inverted = false
	this.horizontal = horizontal
	this.otherSide = otherSide
	this.bounds = new Rectangle()

	this.position = 0

	this.format = new Format(chart)
	this.format.stroke.size = 2
	this.format.depth = 0.2

	this.custom = false

	this.z = otherSide ? 1 : 0

	this.maxLabelDepth = 0

	/**
	 * @constructor
	 * @public
	 * @class Displays text to annotate axes
	 * @param {Tee.Chart} chart The chart object this axis labels object belongs to.
	 * @property {Format} format Defines the visual properties to paint the axis title.
	 * @property {Boolean} [visible=true] Determines if axis labels will be displayed or not.
	 * @property {String} dateFormat="shortDate" Configures string format for date & time labels
	 * @property {Number} rotation=0 Defines the label rotation angle from 0 to 360 degree.
	 * @property {String} labelStyle="auto" Determines label contents from series data ("auto", "value", "mark", "text").
	 * @property {Number} decimals=2 Defines the number of decimals for floating-point numeric labels.
	 * @property {Boolean} alternate=false When true, labels are displayed at alternate positions to fit more labels in the same space.
	 * @property {String} wordWrap="no" Determines if replace label white spaces with new line ("auto", "yes", "no").
	 */
	function Labels(chart, axis) {
		this.chart = chart
		this.format = new Format(chart)
		this.decimals = 2
		this.fixedDecimals = false
		this.padding = 4
		this.separation = 10 // %
		this.visible = true
		this.rotation = 0
		this.alternate = false
		this.maxWidth = 0

		this.wordWrap = "no" // auto,yes,no

		this.roundFirst = true
		this.labelStyle = "auto" // auto,value,mark,text

		this.dateFormat = "shortDate"

		this.checkStyle = function () {
			var st = this.labelStyle,
				s = axis.firstSeries

			this._text = null
			this._textlabels = null

			if (st == "auto") {
				if (s.data.labels.length > 0 && s.associatedToAxis(axis) && axis.horizontal == s.notmandatory.horizontal) {
					this._text = s

					/*    if ((this.chart.series.items.length > 1) && (this.chart.series.items[0] instanceof Tee.Bar) 
																	&& (this.chart.series.items[0].stacked == "sideAll")){
			
			var li=chart.series.items, t, tt, ser;
			this._textlabels=this._text.data.labels;
			for(t=1; ser=li[t++];){
					if (ser.visible && ser.associatedToAxis(axis)) {
					for(tt=0; tt < ser.data.values.length; tt++){  
						this._textlabels.push(ser.data.labels[tt]);
						}
						}
							}

							if (s === undefined)
								s="";
					}*/
				}
			} else if (st == "mark" || st == "text") this._text = s
		}

		this.formatValueString = function (value) {
			if (this.valueFormat) {
				var DecimalSeparator = Number("1.2").toLocaleString().substr(1, 1)

				var AmountWithCommas = (value * 1).toLocaleString()
				var arParts = String(AmountWithCommas).split(DecimalSeparator)
				var intPart = arParts[0]

				var padding = ""
				if (this.decimals > 0)
					for (var i = 0; i < this.decimals; i++) {
						padding = padding + "0"
					}

				var decPart = arParts.length > 1 ? arParts[1] : ""
				decPart = (decPart + padding).substr(0, this.decimals)

				if (decPart.length > 0) return intPart + DecimalSeparator + decPart
				else return intPart
			} else return value.toFixed(this.decimals)
		}

		/**
		 * @returns {String} Returns the series label that corresponds to a given value, (or the value if no label exists).
		 */
		this.getLabel = function (value) {
			var v = trunc(value),
				s,
				data

			if (this._text && v == value) {
				data = this._text.data

				if (data.x) v = data.x.indexOf(v)

				var li = chart.series.items,
					t,
					ser

				//specific case: check for non-std sideAll labelling
				if (li.length > 0 && li[0] instanceof Tee.Bar && li[0].stacked == "sideAll") {
					s = data.labels[value]
				} else {
					s = data.labels[v]
				}

				// Last resort, try to find labels from any series in axis:

				if (!s) {
					for (t = 0; t < li.length; t++) {
						ser = li[t]
						if (!ser) break
						if (ser != this._text) {
							if (ser.visible && ser.associatedToAxis(axis)) {
								s = ser.data.labels[v]
								if (s) break
							}
						}
					}

					if (s === undefined) s = ""
				}
			} else if (axis.dateTime) {
				if (Date.prototype.format)
					// script: src/date.format.js
					s = new Date(value).format(this.dateFormat)
				else s = new Date(value).toDateString() // fallback
			} else s = this.formatValueString(value)

			if (this.wordWrap != "no" && this.wordWrap != false) {
				s = s.replace(/ /g, "\n")
			}

			if (this.ongetlabel) {
				s = this.ongetlabel(value, s)
				this.format.font.prepare() // <-- in case user code at ongetlabel event has changed the font.
			}

			return "" + s
		}

		/**
		 * @returns {Number} Returns the width in pixels of value converted to string.
		 */
		this.width = function (value) {
			var oldFixDec = this.fixedDecimals //check widest label when sizing
			this.fixedDecimals = true
			var tmpWidth = this.format.textWidth(this.getLabel(value))
			this.fixedDecimals = oldFixDec
			return tmpWidth
		}
	}

	this.labels = new Labels(chart, this)
	var f = this.labels.format.font

	/**
	 * Changes the axis maximum and minimum values
	 * @param {Number} delta The positive or negative amount to scroll.
	 */
	this.scroll = function (delta) {
		this.automatic = false
		if (this.inverted) delta = -delta
		this.minimum += delta
		this.maximum += delta
	}

	if (horizontal) {
		f.textAlign = "center"
		f.baseLine = otherSide ? "bottom" : "top"
	} else {
		f.textAlign = otherSide ? "left" : "right"
		f.baseLine = "middle"
	}

	/**
	 * @constructor
	 * @class Format and parameters to display a grid of lines at axes increments
	 * @param {Tee.Chart} chart The chart object this axis grid object belongs to.
	 * @property {Format} format Visual properties to draw axis grids.
	 * @property {Boolean} [visible=true] Determines if grid lines will be displayed or not.
	 * @property {Boolean} [centered=false] Determines if grid lines are displayed at axis label positions or at middle between labels.
	 * @property {Boolean} [lineDash=false] Draws grid lines using dash-dot segments or solid lines.
	 */
	function Grid(chart) {
		this.chart = chart
		var f = (this.format = new Format(chart))
		f.stroke.fill = "silver"
		f.stroke.cap = "butt"
		f.fill = ""
		this.visible = true
		this.lineDash = false
	}

	this.grid = new Grid(chart)

	/**
	 * @constructor
	 * @class Stroke parameters to draw small lines at axes labels positions
	 * @param {Tee.Chart} chart The chart object this axis ticks object belongs to.
	 * @property {Tee.Stroke} stroke Defines the visual attributes used to draw the axis ticks.
	 * @property {Number} [length=4] The length of ticks in pixels.
	 */
	function Ticks(chart) {
		this.chart = chart
		this.stroke = new Stroke(chart)
		this.stroke.cap = "butt"
		this.visible = true
		this.length = 4
	}

	this.ticks = new Ticks(chart)

	this.innerTicks = new Ticks(chart)
	this.innerTicks.visible = false

	var m = (this.minorTicks = new Ticks(chart))
	m.visible = false
	m.length = 2
	m.count = 3

	/**
	 * @constructor
	 * @augments Annotation
	 * @class Text and formatting properties to display near axes
	 * @param {Tee.Chart} chart The chart object this axis title object belongs to.
	 * @param {Boolean} [transparent=true] Determines if axis title will be displayed or not.
	 */
	function Title(chart) {
		Annotation.call(this, chart)
		this.padding = 4
		this.transparent = true
		this.rotation = 0
		this.format.font.textAlign = "center"

		this.drawIt = function (textAlign, x, y, rotation) {
			this.format.font.textAlign = textAlign

			if (rotation === 0) {
				this.position.x = x
				this.position.y = y
				this.forceDraw()
			} else {
				var ctx = chart.ctx
				ctx.save()

				ctx.translate(x, y)
				ctx.rotate((-rotation * Math.PI) / 180)

				this.position.x = 0
				this.position.y = 0

				this.forceDraw()
				ctx.restore()
			}
		}
	}

	Title.prototype = new Annotation()

	this.title = new Title(chart)
	if (!horizontal) this.title.rotation = otherSide ? 270 : 90

	this.automatic = true
	this.minimum = 0
	this.maximum = 0
	this.increment = 0
	this.log = false

	this.startPos = 0
	this.endPos = 0

	this.start = 0 // %
	this.end = 100 // %

	this.axisSize = 0

	this.scale = 0
	this.increm = 0

	function calcWordWrap(f, s) {
		var r = 0,
			w,
			ss = s.split(" "),
			t

		for (t = 0; t < ss.length; t++) {
			w = f.textWidth(ss[t])
			if (w > r) r = w
		}

		return r
	}

	function toRadians(angle) {
		return angle * (Math.PI / 180)
	}

	/**
	 * @returns {Number} Returns the approximated width in pixels of largest axis label.
	 */
	this.minmaxLabelWidth = function (adjust) {
		var l = this.labels,
			f = l._text,
			w = 0,
			s,
			wordWrap
		var la = this.labels,
			l

		if (f !== null && f !== undefined) {
			wordWrap = l.wordWrap == "auto" || l.wordWrap == "yes"

			la.format.font.prepare()

			for (var t = 0, le = f.data.labels.length; t < le; t++) {
				s = f.data.labels[t]
				if (l.ongetlabel) s = l.ongetlabel(t, s)

				if (s) w = Math.max(w, wordWrap ? calcWordWrap(l.format, s) : l.format.textWidth(s))
			}
		} else {
			var mi = this.roundMin(),
				ma = this.maximum
			w = Math.max(l.width(mi), l.width(0.5 * (mi + ma)))

			w = Math.max(w, l.width(0.0000001))
			w = Math.max(w, l.width(ma))
		}

		//adjust for rotation (if any). Are we adjusting or spacing?
		if (l.rotation == 0) {
			if ((this.horizontal && adjust) || (!this.horizontal && !adjust)) {
				w = la.format.textHeight("Wj")
			}
		} else if (this.horizontal) {
			if (adjust) {
				w = Math.abs(Math.sin(toRadians(l.rotation)) * w)
			} else {
				w = Math.abs(Math.cos(toRadians(l.rotation)) * w)
			}
		} else {
			if (adjust) {
				w = Math.abs(Math.cos(toRadians(l.rotation)) * w)
			} else {
				w = Math.abs(Math.sin(toRadians(l.rotation)) * w)
			}
		}

		//guarantee minimum spacing
		if (w < la.format.textHeight("Wj")) w = la.format.textHeight("Wj")
		return w
	}

	this.checkRange = function () {
		if (this.maximum - this.minimum < this.minAxisRange) this.maximum = this.minimum + this.minAxisRange
	}

	this.checkMinMax = function () {
		var s = this.chart.series,
			h = this.horizontal

		if (this.automatic) {
			this.minimum = h ? s.minXValue(this) : s.minYValue(this)
			this.maximum = h ? s.maxXValue(this) : s.maxYValue(this)

			this.checkRange()
		}
	}

	/**
	 * @returns {Number} Returns if any visible series has less than n values.
	 * Only called from Axis calcIncrement, to avoid axis label increments to be
	 * smaller than series number of points.
	 */
	function anySeriesHasLessThan(c, n) {
		var t, s, isY

		for (t = 0; t < c.chart.series.items.length; t++) {
			s = c.chart.series.items[t]
			if (!s) break
			if (s.visible && s.sequential) {
				isY = s.yMandatory

				if ((isY && c.horizontal) || (!isY && !c.horizontal)) {
					if (s.associatedToAxis(c)) {
						if (s.count() <= n) return true
					}
				}
			}
		}

		return false
	}

	/**
	 * @returns {Number} Returns the next bigger value in the sequence 1,2,5,10,20,50...
	 */
	function nextStep(value) {
		if (!isFinite(value)) return 1
		else if (value >= 10) return 10 * nextStep(0.1 * value)
		else if (value < 1) return 0.1 * nextStep(value * 10)
		else return value < 2 ? 2 : value < 5 ? 5 : 10
	}

	/**
	 * @returns {Number} Returns the best appropriate distance between axis labels.
	 */
	function calcIncrement(c, maxLabelSize) {
		if (c.maximum == c.minimum) return 1
		else {
			var tmp = c.axisSize / maxLabelSize,
				less = anySeriesHasLessThan(c, tmp)
			tmp = Math.abs(c.maximum - c.minimum) / (tmp + 1)
			return less ? Math.max(1, tmp) : nextStep(tmp)
		}
	}

	this.calcAxisScale = function () {
		var range = this.maximum - this.minimum
		if (range === 0) range = 1
		else if (this.log) range = Math.log(range)

		this.scale = this.axisSize / range
	}

	this.calcScale = function () {
		var la = this.labels,
			l

		la.format.font.prepare()
		l = this.minmaxLabelWidth(false)

		if (la.alternate) l *= 0.5

		l *= 1 + la.separation * 0.02

		this.increm = this.increment === 0 ? calcIncrement(this, l) : this.increment

		if (this.increm <= 0) this.increm = 0.1
		else if (this.increm > 0 && this.increm <= 0.00000001) this.increm = 0.00000001
	}

	/**
	 * @returns {Boolean} Returns the first visible series associated to this axis, or null if any.
	 */
	this.hasAnySeries = function () {
		var li = this.chart.series.items,
			t,
			s,
			d,
			h

		for (t = 0; t < li.length; t++) {
			s = li[t]
			if (!s) break
			// DB fix Sep-2013, empty series should not be considered:
			if (s.visible && s.associatedToAxis(this) && (s.__alwaysDraw || s.count() > 0)) {
				// Remember now if series "s" has date-time values:

				h = this.horizontal
				if (s.yMandatory) h = !h

				d = h ? s.data.values : s.data.x
				this.dateTime = d && d.length > 0 && d[0] instanceof Date

				return s
			}
		}

		return null
	}

	this.drawAxis = function () {
		var f = this.format,
			c = this.chart.ctx,
			pos = this.axisPos,
			start = this.startPos,
			end = this.endPos

		var rad = 20 * f.depth,
			r

		if (this.chart.aspect.view3d && rad > 0) {
			if (horizontal) r = { x: start, y: pos - rad * 0.5, width: end - start, height: rad }
			else r = { x: pos - rad * 0.5, y: start, width: rad, height: end - start }

			var old = this.z
			f.z = old - f.depth * 0.5
			f.cylinder(r, 1, !horizontal)
			f.draw(c, null, r)
			f.z = old
		} else {
			c.z = this.z

			c.beginPath()

			if (horizontal) {
				c.moveTo(start, pos)
				c.lineTo(end, pos)
			} else {
				c.moveTo(pos, start)
				c.lineTo(pos, end)
			}

			f.stroke.prepare()
			c.stroke()
		}
	}

	this.drawGrids = function () {
		var c = this.chart.ctx,
			p,
			r = this.chart.chartRect,
			f = this.grid.format,
			x1,
			y1,
			x2,
			y2,
			v,
			vmin = this.roundMin()

		if (this.grid.centered) {
			var tmp = this.increm * 0.5
			if (vmin - tmp >= this.minimum) vmin -= tmp
			else vmin += tmp
		}

		var a = this.chart.aspect,
			is3d = a.view3d,
			isOrtho = is3d && a.orthogonal,
			off3d = is3d ? 1 : 0

		c.beginPath()

		if (horizontal) {
			y1 = this.bounds.y - off3d
			y2 = otherSide ? r.getBottom() - 1 : r.y + 1
		} else {
			x1 = this.bounds.x + off3d
			x2 = otherSide ? r.x + 1 : r.getRight() - 1
		}

		var oldpos,
			pos = -1

		if (f.fill !== "") {
			v = vmin

			var old = f.stroke.fill
			f.stroke.fill = ""

			while (v <= this.maximum) {
				p = this.calc(v)

				if (pos % 2 === 0)
					horizontal ? f.rectangle(oldpos, y2, p - oldpos, y1 - y2) : f.rectangle(x1, oldpos, x2 - x1, p - oldpos)

				oldpos = p

				v += this.increm
				pos++
			}

			f.stroke.fill = old
			c.fillStyle = ""
		}

		v = vmin

		if (isOrtho)
			if (horizontal) {
				y1 -= a._orthoy
				y2 -= a._orthoy
			} else {
				x1 += a._orthox
				x2 += a._orthox
			}

		var isCustomDash = f.stroke.dash && !c.setLineDash && !c.mozCurrentTransform

		c.z = is3d ? this.chart.walls.back.format.z - 0.01 : 0

		while (v <= this.maximum) {
			pos = this.calc(v)

			horizontal ? (x1 = x2 = pos) : (y1 = y2 = pos)

			if (isOrtho) {
				if (horizontal) {
					x1 += a._orthox
					x2 += a._orthox
				} else {
					y1 -= a._orthoy
					y2 -= a._orthoy
				}
			}

			// TODO: lineZ (3D grid sides)

			if (is3d && !this.otherSide && c.lineZ) c.lineZ(x1, y1, 0, c.z)

			if (isCustomDash) dashedLine(c, x1, y1, x2, y2)
			else {
				c.moveTo(x1, y1)
				c.lineTo(x2, y2)
			}

			v += this.increm
		}

		f.stroke.prepare()
		f.shadow.prepare(c)

		c.stroke()
	}

	function truncFloat(n) {
		return n - (n % 1)
	}

	/**
	 * @returns {Number} Returns the axis minimum value rounded according the axis increment distance.
	 */
	this.roundMin = function () {
		// OLD: var v=trunc(this.minimum/this.increm);

		// NEW: Fix against rounding precision of "trunc" (|0) operator:

		if (this.increm === 0 || !this.labels.roundFirst) return this.minimum
		else {
			//var v = parseFloat(new Number(this.minimum/this.increm).toFixed(0));

			var v = truncFloat(this.minimum / this.increm)

			return this.increm * (this.minimum <= 0 ? v : 1 + v)
		}
	}

	this.drawTicks = function (t, factor, mult) {
		var v = this.roundMin(),
			tl = 1 + t.length,
			tl2 = 1

		if ((horizontal && otherSide) || (!horizontal && !otherSide)) {
			tl = -tl
			tl2 = -1
		}

		tl *= factor
		tl2 *= factor

		tl += this.axisPos
		tl2 += this.axisPos

		var p,
			inc,
			n = 1,
			cou = 0

		if (mult === 0) inc = this.increm
		else {
			n += mult
			inc = this.increm / n
		}

		var c = this.chart.ctx

		c.beginPath()

		while (v <= this.maximum) {
			if (mult === 0 || cou++ % n !== 0) {
				p = this.calc(v)

				if (horizontal) {
					c.moveTo(p, tl2)
					c.lineTo(p, tl)
				} else {
					c.moveTo(tl2, p)
					c.lineTo(tl, p)
				}
			}

			v += inc
		}

		t.stroke.prepare()

		t.z = this.z
		c.z = t.z

		c.stroke()
	}

	this.drawTitle = function () {
		var l = this.labels,
			tmpX,
			tmpY,
			titleBounds = this.title.bounds,
			rotation = this.title.rotation,
			//ctx=this.chart.ctx,
			textAlign = "center"

		if (this.title.text !== "") {
			if (horizontal) {
				tmpY = this.title.padding

				if (this.ticks.visible) tmpY += this.ticks.length

				if (l.visible) {
					l.format.font.prepare()
					var h = this.maxLabelDepth
					if (l.alternate) h *= 2
					tmpY += h
				}

				tmpX = this.startPos + this.axisSize * 0.5

				if (this.otherSide) tmpY = -tmpY - titleBounds.height

				tmpY = this.axisPos + tmpY

				if (rotation === 0) {
					tmpX -= titleBounds.width * 0.5
				} else {
					tmpX += titleBounds.height * (this.otherSide ? -0.5 : 0.5)
					if (!this.otherSide) tmpY += 1.5 * titleBounds.height
					textAlign = this.otherSide ? "near" : rotation === 270 ? "near" : "far"
				}
			} else {
				tmpX = this.title.padding

				if (this.ticks.visible) tmpX += this.ticks.length

				if (l.visible) {
					var w = l.maxWidth
					if (l.alternate) w *= 2
					tmpX += w
				}

				tmpY = this.startPos + 0.5 * this.axisSize

				if (rotation === 0) {
					tmpX = this.axisPos + (this.otherSide ? tmpX : -tmpX)
					tmpY -= 0.5 * titleBounds.height
					textAlign = this.otherSide ? "near" : "far"
				} else {
					tmpX += titleBounds.height
					tmpX = this.axisPos + (this.otherSide ? tmpX : -tmpX)
					tmpY += titleBounds.width * (this.otherSide ? -0.5 : 0.5)
				}
			}

			this.title.drawIt(textAlign, tmpX, tmpY, rotation)

			//      chart.ctx.rect(tmpX,tmpY,titleBounds.width,titleBounds.height);
			//      chart.ctx.stroke();
		}
	}

	this.rotatedWidth = (l, w) => Math.abs(Math.sin(toRadians(l.rotation)) * w)

	this.drawLabel = function (value, r) {
		var l = this.labels,
			s = l.getLabel(value)

		/*
	if (this.firstSeries && (this.firstSeries.notmandatory==this))
			s=l.getLabel(this.firstSeries,value);
	else
			s=l.getLabel(null,value);
	*/

		r.width = l.format.textWidth(s)
		if (r.width > l.maxWidth) l.maxWidth = r.width

		if (l.rotation == 0) this.horizontal ? (r.x -= 0.5 * r.width) : (r.y += r.height * 0.5)
		else {
			var c = this.chart.ctx
			c.save()
			c.translate(r.x, r.y)
			c.rotate((-Math.PI * l.rotation) / 180)
			c.textAlign = "right"

			if (this.horizontal) {
				var xDisplacement = this.rotatedWidth(l, r.width) * 0.5
				var yDisplacement = this.rotatedWidth(l, r.height) * 0.5 - 4
				if (l.rotation >= 220) this.otherSide ? (r.x = xDisplacement * -1) : (r.x = xDisplacement)
				else this.otherSide ? (r.x = xDisplacement + 4) : (r.x = xDisplacement * -1)

				this.otherSide ? (r.y = yDisplacement) : (r.y = yDisplacement * -1)
			} else {
				var xDisplacement = this.rotatedWidth(l, r.width)
				//var yDisplacement = (this.rotatedWidth(l, r.height));

				this.otherSide ? (r.x = xDisplacement) : (r.x = 30)
				r.y = -8
			}
		}

		if (l.format.font.textAlign == "right") r.x -= r.width

		l.format.z = this.z * this.chart.walls.back.format.z

		l.format.drawText(r, s)

		if (l.rotation !== 0) this.chart.ctx.restore()
	}

	this.drawLabels = function () {
		var v = this.roundMin(),
			r = new Rectangle(),
			c = this.axisPos,
			l = this.labels
		//var v=(this.minimum<this.maximum) ? this.roundMin() : this.minimum, r=new Rectangle(), c=this.axisPos, l=this.labels;

		l.maxWidth = 0
		l.format.font.prepare()

		var tl = this.ticks.visible ? this.ticks.length : 0

		tl += l.padding

		if (this.horizontal) this.otherSide ? (c -= tl) : (c += tl)
		else this.otherSide ? (c += tl) : (c -= tl)

		var oldc = c

		r.height = l.format.textHeight("Wj")

		var alter = l.alternate,
			w = alter ? (this.horizontal ? -this.minmaxLabelWidth(true) : this.minmaxLabelWidth(true)) : 0,
			p

		// TODO: if ongetlabels .... (loop for user-custom labels position and text)

		while (v <= this.maximum) {
			p = this.calc(v)

			if (this.horizontal) {
				r.x = p
				r.y = c // -r.height*0.5;
			} else {
				r.x = c
				r.y = p - r.height * 0.5
			}

			if (alter) c = c == oldc ? (this.otherSide ? oldc + w : oldc - w) : oldc

			this.drawLabel(v, r)
			v += this.increm
		}
	}

	/**
	 * @returns {Number} Returns the position in pixels for a given value, using the axis scales.
	 */
	this.calc = function (value) {
		var p

		if (value !== value)
			// isNaN() <-- slow
			p = 0
		else if (this.log) {
			value -= this.minimum
			p = value <= 0 ? 0 : Math.log(value) * this.scale
		} else p = (value - this.minimum) * this.scale

		if (this.horizontal) return this.inverted ? this.endPos - p : this.startPos + p
		else return this.inverted ? this.startPos + p : this.endPos - p
	}

	/**
	 * @returns {Number} Returns the axis value for a given position in pixels.
	 */
	this.fromPos = function (p) {
		var i = this.horizontal
		if (this.inverted) i = !i
		return this.minimum + (i ? p - this.startPos : this.endPos - p) / this.scale
	}

	this.fromSize = function (p) {
		return p / this.scale
	}
	/**
	 * @returns {Number} Returns the index of a given position in pixels (only works in bottom axes).
	 */
	this.fromSizeCalcIndex = function (p) {
		var index = -1
		if (this.dateTime == true) {
			var i = 0,
				inc = 0,
				actualValue = ((this.maximum - this.minimum) / this.axisSize) * p + this.minimum

			if (this.chart.series.items[0].data.x.length > 1) {
				inc = this.chart.series.items[0].data.x[1].getTime() - this.chart.series.items[0].data.x[0].getTime()
			}
			while (index == -1 && i < this.chart.series.items[0].data.x.length) {
				if (
					this.chart.series.items[0].data.x[i].getTime() + inc / 2 > actualValue &&
					actualValue + inc / 2 > this.chart.series.items[0].data.x[i].getTime()
				) {
					index = i
				} else i++
			}
		} else {
			index = p / this.scale + this.minimum
			if (this.chart.series.items[0].data.values.length < index || index < -0.5) index = -1
		}
		return index
	}
	/**
	 * @returns {Number} Returns the size in pixels of a given value, using the axis scales.
	 */
	this.calcSize = function (value) {
		return Math.abs(this.calc(value) - this.calc(0))
	}

	/**
	 * @param {Number} p1 Position in pixels to be axis minimum.
	 * @param {Number} p2 Position in pixels to be axis maximum.
	 */
	this.calcMinMax = function (p1, p2) {
		this.automatic = false

		var a = this.fromPos(p1),
			b = this.fromPos(p2),
			tmp
		if (a > b) {
			tmp = a
			a = b
			b = tmp
		}

		this.minimum = a
		this.maximum = b

		this.checkRange()
	}

	this.minAxisRange = 0.0000000001

	this.setMinMax = function (min, max) {
		this.automatic = false

		this.minimum = min
		this.maximum = max

		this.checkRange()
	}
}

Axis.adjustRect = function () {
	var s = 0,
		l = this.labels,
		b = this.chart.chartRect,
		ti = this.title,
		hasTitle

	// Recalc firstSeries even if this axis is not visible:
	this.firstSeries = this.hasAnySeries()

	if (!this.visible) return

	if (this.firstSeries && this.visible) {
		this.checkMinMax()

		l.checkStyle()

		hasTitle = ti.shouldDraw()
		if (hasTitle) ti.resize()

		if (b.automatic && !this.custom) {
			if (l.visible) {
				l.format.font.prepare()
				s = this.minmaxLabelWidth(true)
				this.maxLabelDepth = s

				if (l.alternate) s *= 2
				s += l.padding
			}

			if (this.ticks.visible) s += this.ticks.length

			if (hasTitle) s += ti.bounds.height //+ti.padding;

			if (this.horizontal) this.otherSide ? b.setTop(b.y + s) : (b.height -= s)
			else this.otherSide ? (b.width -= s) : b.setLeft(b.x + s)
		}
	}
}

Axis.prototype.setPos = function (a, b) {
	this.startPos = a + this.start * b * 0.01
	this.endPos = a + this.end * b * 0.01
	this.axisSize = this.endPos - this.startPos
}

/**
 * Axis rect calcs. Returns current ChartRect.
 */
Axis.calcRect = function () {
	if (!this.firstSeries) return

	this.checkMinMax()

	// Calc bounds
	var b = this.chart.chartRect,
		bo = this.bounds,
		h = this.horizontal

	if (h) {
		bo.y = this.otherSide ? b.y : b.y + b.height
		bo.width = b.width
		this.setPos(b.x, b.width)
	} else {
		bo.x = this.otherSide ? b.x + b.width : b.x
		bo.height = b.height
		this.setPos(b.y, b.height)
	}

	this.calcAxisScale()

	var tmp = this.chart.series

	//specific case: check for non-std sideAll scaling
	if (tmp.items.length > 0) {
		if (this.automatic) {
			var s = tmp.items[0]

			if (s instanceof Tee.Bar && s.notmandatory == this && s.stacked == "sideAll") {
				s.notmandatory.minimum = -0.5
				s.notmandatory.maximum = s.countAll(false) - 0.5
			}
		}
	}

	// Calculate axis margins:
	if (this.automatic) {
		var s = this.chart.series,
			m = h ? s.horizMargins() : s.vertMargins(),
			hasX = m.x > 0,
			hasY = m.y > 0

		if (hasX) this.minimum -= this.fromSize(m.x)

		if (hasY) this.maximum += this.fromSize(m.y)

		if (hasX || hasY) this.calcAxisScale()
	}

	this.calcScale()

	// Calc pos

	var v = (h ? b.height : b.width) * this.position * 0.01,
		w = this.chart.walls,
		wallsize = 0,
		haswalls = w.visible && this.chart.aspect.view3d

	if (h) {
		if (haswalls && w.bottom.visible) wallsize = w.bottom.size
		this.axisPos = this.otherSide ? b.y + v : b.getBottom() + wallsize - v
	} else {
		if (haswalls && w.left.visible) wallsize = w.left.size
		this.axisPos = this.otherSide ? b.getRight() - v : b.x - wallsize + v
	}
}

Axis.draw = function () {
	if (this.visible && this.firstSeries) {
		this.z = this.otherSide ? this.chart.walls.back.format.z : 0

		//guarantee centre label for one-value axes.
		if (Math.abs(this.maximum - this.minimum) <= 0.000000001 /*widen beyond this.minAxisRange*/) {
			var diff = Math.abs(this.minimum) < 0.000000001 ? 0.00000001 : this.minimum / 10000000

			if (this.minimum != this.minimum - diff || this.maximum != this.minimum + diff) {
				this.setMinMax(this.minimum - diff, this.maximum + diff)
				if (this.chart != null) this.chart.draw()
			}
		}

		if (this.format.stroke.fill !== "") this.drawAxis()

		// Protect against infinite loop:
		if (this.roundMin() + 1000 * this.increm > this.maximum) {
			if (this.grid.visible) this.drawGrids()
			if (this.ticks.visible) this.drawTicks(this.ticks, 1, 0)
			if (this.innerTicks.visible) this.drawTicks(this.innerTicks, -1, 0)
			if (this.minorTicks.visible) this.drawTicks(this.minorTicks, 1, Math.max(0, this.minorTicks.count))
			if (this.labels.visible) this.drawLabels()
		}

		if (this.title.shouldDraw()) this.drawTitle()
	}
}

export { Axis }
